<template>
  <v-app class="referral-wow-layout">
    <div class="ambient-glow glow-primary"></div>
    <div class="ambient-glow glow-secondary"></div>

    <v-overlay :value="loading" absolute color="#020202" opacity="1" z-index="100">
      <v-progress-circular indeterminate color="primary" size="80" width="4"></v-progress-circular>
    </v-overlay>

    <v-container v-if="!loading && (error || !studios || studios.length === 0)" class="fill-height justify-center relative-z px-4">
      <div class="glass-card pa-10 rounded-xl text-center inactive-box">
        <v-icon size="80" color="error" class="mb-6 pulse-error">mdi-alert-decagram-outline</v-icon>
        <h2 class="text-h4 font-weight-black text-uppercase mb-4 white--text">Hoppla!</h2>
        <p class="text-body-1 grey--text text--lighten-1 mb-10 mx-auto" style="max-width: 350px">
          Leider konnten wir die Aktion nicht laden. Der Link ist möglicherweise abgelaufen oder fehlerhaft.
        </p>
        <v-btn color="primary" x-large rounded outlined class="font-weight-bold px-10 btn-hover-glow" @click="toHomepage"> Zur Startseite </v-btn>
      </div>
    </v-container>

    <v-container v-else-if="!loading && submitted" class="fill-height justify-center relative-z px-4">
      <div class="glass-card pa-12 rounded-xl text-center inactive-box">
        <div class="success-icon-container mx-auto mb-8">
          <div class="success-glow"></div>
          <v-icon color="primary" size="60">mdi-check-all</v-icon>
        </div>
        <h2 class="text-h3 font-weight-black white--text mb-4 tracking-tight">Anfrage <span class="primary--text">erhalten!</span></h2>
        <p class="text-h6 grey--text text--lighten-1 mb-10 mx-auto font-weight-regular lh-relaxed" style="max-width: 450px">
          Wir freuen uns auf dich! Wir haben deine Daten erhalten und melden uns in Kürze bei dir.
        </p>
        <v-btn color="primary" x-large rounded depressed class="font-weight-black px-12 action-btn pulse-glow" @click="toHomepage">
          Zurück zur Startseite
        </v-btn>
      </div>
    </v-container>

    <v-container v-else class="fill-height align-start align-md-center justify-center relative-z px-4 py-12">
      <div class="form-wrapper w-100" style="max-width: 650px">
        <v-card class="glass-card rounded-xl overflow-hidden" dark elevation="24">
          <div class="form-header text-center pa-8 pa-sm-10 pb-4">
            <transition name="fade" mode="out-in">
              <v-img
                v-if="selectedStudio && selectedStudio.code"
                :key="selectedStudio.code"
                :src="`https://app.wellcomefit.ch/img/logos/studio/WELL-COME-FIT_LOGO_${selectedStudio.code.toUpperCase()}_BLACK.png`"
                max-width="160"
                contain
                class="mx-auto mb-8 logo-pop"></v-img>
              <div v-else class="generic-logo-placeholder mb-8 d-flex justify-center">
                <v-icon size="60" color="primary">mdi-star-shooting-outline</v-icon>
              </div>
            </transition>

            <div v-if="referralMail" class="vip-badge d-inline-flex align-center px-5 py-2 rounded-pill mb-6">
              <v-icon small color="primary" class="mr-2">mdi-ticket-account</v-icon>
              <span class="text-caption font-weight-bold text-uppercase tracking-widest white--text">
                Einladung von: <span class="primary--text">{{ referralMail }}</span>
              </span>
            </div>

            <h1 class="text-h4 font-weight-black text-uppercase white--text tracking-tight mb-2">Bring a Friend</h1>
            <p class="text-subtitle-1 grey--text text--lighten-1 font-weight-regular font-italic">
              Lass dich von uns für weitere Infos kontaktieren.
            </p>
          </div>

          <v-card-text class="pa-6 pa-sm-10 pt-0">
            <v-form v-model="isFormValid" ref="leadForm">
              <v-autocomplete
                v-if="studios.length > 1"
                v-model="selectedStudio"
                :items="studios"
                item-text="name"
                return-object
                label="Wähle dein Wunsch-Studio (Suchen nach Name, PLZ, Ort...)*"
                outlined
                dark
                hide-details
                prepend-inner-icon="mdi-domain"
                color="primary"
                class="glass-input mb-4"
                :rules="[rules.required]"
                :filter="customStudioFilter"
                no-data-text="Leider kein Studio gefunden">
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">{{ item.name }}</v-list-item-title>
                    <v-list-item-subtitle class="text--lighten-1 mt-1">
                      <v-icon small class="mr-1">mdi-map-marker-outline</v-icon>
                      {{ item.adresse }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>

              <transition name="fade-slide">
                <div v-if="leadSettings">
                  <v-row>
                    <v-col cols="12" md="6" class="pb-0 pb-md-3" v-if="leadSettings.leadFields.name">
                      <v-text-field
                        v-model="leadInformation.name"
                        label="Vorname*"
                        :rules="[rules.required]"
                        outlined
                        hide-details
                        dark
                        color="primary"
                        class="glass-input"
                        prepend-inner-icon="mdi-account-outline"></v-text-field>
                    </v-col>

                    <v-col cols="12" md="6" class="pt-0 pt-md-3" v-if="leadSettings.leadFields.surname">
                      <v-text-field
                        v-model="leadInformation.surname"
                        label="Nachname*"
                        hide-details
                        :rules="[rules.required]"
                        outlined
                        dark
                        color="primary"
                        class="glass-input"
                        :prepend-inner-icon="$vuetify.breakpoint.mdAndUp ? '' : 'mdi-account-details-outline'"></v-text-field>
                    </v-col>
                  </v-row>

                  <v-text-field
                    v-if="leadSettings.leadFields.phone"
                    v-model="leadInformation.phone"
                    label="Telefonnummer*"
                    :rules="[rules.required]"
                    outlined
                    hide-details
                    dark
                    color="primary"
                    class="glass-input"
                    prepend-inner-icon="mdi-phone-outline"></v-text-field>

                  <v-text-field
                    v-if="leadSettings.leadFields.email"
                    v-model="leadInformation.email"
                    label="E-Mail*"
                    :rules="[rules.required, rules.email]"
                    outlined
                    hide-details
                    dark
                    color="primary"
                    class="glass-input"
                    prepend-inner-icon="mdi-email-outline"></v-text-field>

                  <v-textarea
                    v-if="leadSettings.leadFields.message"
                    v-model="leadInformation.message"
                    label="Deine Nachricht (Optional)"
                    outlined
                    dark
                    color="primary"
                    hide-details
                    rows="3"
                    no-resize
                    class="glass-input"
                    prepend-inner-icon="mdi-message-text-outline"></v-textarea>
                </div>

                <div v-else class="text-center py-6">
                  <v-icon size="40" color="grey darken-2" class="mb-3">mdi-cursor-default-click-outline</v-icon>
                  <p class="grey--text">Bitte wähle oben ein Studio aus, um das Formular zu laden.</p>
                </div>
              </transition>

              <div class="conditions-glass pa-4 rounded-lg mt-2 mb-6 text-caption grey--text text--lighten-1 lh-relaxed">
                *Angebot nicht kumulierbar / kein Firmenrabatt zusätzlich / nach Abonnementkündigung verfällt die Gutschrift / keine Barauszahlung des
                Betrags
              </div>
            </v-form>

            <div v-if="leadSettings && leadSettings.showContactInfo" class="mt-8 text-center">
              <div class="divider-wrapper mb-6">
                <span class="divider-text text-uppercase font-weight-bold tracking-widest grey--text text--darken-1">Oder Direkt</span>
              </div>
              <div class="d-flex flex-wrap justify-center gap-4">
                <v-btn
                  v-if="selectedStudio.telefon"
                  :href="'tel:' + selectedStudio.telefon"
                  outlined
                  rounded
                  color="grey lighten-1"
                  class="lowercase contact-btn">
                  <v-icon left size="18">mdi-phone</v-icon> {{ selectedStudio.telefon }}
                </v-btn>
                <v-btn
                  v-if="selectedStudio.email"
                  :href="'mailto:' + selectedStudio.email"
                  outlined
                  rounded
                  color="grey lighten-1"
                  class="lowercase contact-btn">
                  <v-icon left size="18">mdi-email</v-icon> {{ selectedStudio.email }}
                </v-btn>
              </div>
            </div>
          </v-card-text>

          <div class="pa-6 pa-sm-10 pt-0">
            <v-btn
              :disabled="!isFormValid || !selectedStudio"
              :loading="submitting"
              @click="sendLead"
              color="primary"
              x-large
              block
              class="font-weight-black text-uppercase action-btn pulse-glow"
              style="height: 64px; border-radius: 16px"
              elevation="0">
              <span class="text-h6">Jetzt Absenden</span>
              <v-icon right size="28" class="ml-2">mdi-send</v-icon>
            </v-btn>
          </div>

          <div class="footer-glass pa-6 text-center">
            <div class="d-flex justify-center mb-6 gap-4">
              <v-btn icon large color="grey lighten-1" class="social-icon-btn" @click="openLinkNewWindow('https://www.facebook.com/wellcomefitag/')">
                <v-icon size="28">mdi-facebook</v-icon>
              </v-btn>
              <v-btn icon large color="grey lighten-1" class="social-icon-btn" @click="openLinkNewWindow('https://www.instagram.com/wellcomefit')">
                <v-icon size="28">mdi-instagram</v-icon>
              </v-btn>
              <v-btn icon large color="grey lighten-1" class="social-icon-btn" @click="openLinkNewWindow('https://www.tiktok.com/@wellcomefit')">
                <v-img :src="tiktok" max-width="24" max-height="24" contain style="filter: brightness(0) invert(1); opacity: 0.7"></v-img>
              </v-btn>
            </div>

            <div class="d-flex flex-wrap justify-center gap-2 mb-4">
              <v-btn color="grey darken-1" small text class="footer-link" @click="openLinkNewWindow('https://wellcomefit.ch/impressum')"
                >Impressum</v-btn
              >
              <span class="grey--text text--darken-2 mt-1">•</span>
              <v-btn color="grey darken-1" small text class="footer-link" @click="openLinkNewWindow('https://wellcomefit.ch/datenschutz')"
                >Datenschutz</v-btn
              >
              <span class="grey--text text--darken-2 mt-1">•</span>
              <v-btn color="grey darken-1" small text class="footer-link" @click="openLinkNewWindow('https://wellcomefit.ch/agb')">AGB</v-btn>
            </div>

            <div class="text-caption grey--text text--darken-2 font-weight-medium">© {{ currentYear }} well come FIT. All rights reserved.</div>
          </div>
        </v-card>
      </div>
    </v-container>
  </v-app>
</template>

<script>
import api from "../../../../api"
import { mapActions } from "vuex"

export default {
  data() {
    return {
      loading: true,
      submitting: false,
      submitted: false,
      error: false,
      isFormValid: false,
      tiktok: "https://app.wellcomefit.ch/img/logos/socialMedia/tiktok.png",
      studios: [],
      selectedStudio: null,
      leadInformation: {
        name: "",
        surname: "",
        phone: "",
        email: "",
        message: "",
      },
      rules: {
        required: (value) => !!value || "Dieses Feld ist erforderlich.",
        email: (value) => {
          const pattern =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          return !value || pattern.test(value) || "Ungültige E-Mail-Adresse."
        },
      },
    }
  },
  async created() {
    this.setNavigationbarVisibility(false)
    await this.init()
  },
  beforeDestroy() {
    this.setNavigationbarVisibility(true)
  },
  methods: {
    ...mapActions("navigationStoreNotPersisted", ["setNavigationbarVisibility"]),
    customStudioFilter(item, queryText, itemText) {
      if (!queryText) return true

      const searchQuery = queryText.toLowerCase()

      // Hilfsfunktion, um null/undefined Werte sicher abzufangen
      const getSafeText = (value) => (value ? value.toString().toLowerCase() : "")

      const studioName = getSafeText(item.name)
      const studioAddress = getSafeText(item.adresse) // Hier steckt die PLZ und Strasse drin
      const studioCanton = getSafeText(item.canton)

      // Prüft, ob der Suchbegriff in einem der Felder vorkommt
      return studioName.includes(searchQuery) || studioAddress.includes(searchQuery) || studioCanton.includes(searchQuery)
    },
    openLinkNewWindow(link) {
      window.open(link, "_blank")
    },

    toHomepage() {
      window.location.href = "https://wellcomefit.ch"
    },

    async init() {
      try {
        const leadSettingsData = await api.fetchReferralSettingsPublic()
        this.studios = leadSettingsData || []

        if (this.studios.length === 1) {
          this.selectedStudio = this.studios[0]
        }
      } catch (err) {
        this.error = true
        console.error("Fehler beim Laden der Studio-Einstellungen:", err)
      } finally {
        this.loading = false
      }
    },

    async sendLead() {
      this.$refs.leadForm.validate()
      if (!this.isFormValid || !this.selectedStudio) {
        this.$toast.error("Bitte fülle alle Pflichtfelder aus.")
        return
      }

      this.submitting = true
      try {
        const leadPayload = {
          ...this.leadInformation,
          studio: this.selectedStudio._id,
        }
        await api.createReferralLeadPublic(this.leadSettings._id, leadPayload, this.selectedStudio, this.referralMail)
        this.submitted = true
        this.$toast.success("Bäm! Deine Anfrage ist raus. 🚀")
      } catch (e) {
        this.error = true
        this.$toast.error("Es ist ein Fehler aufgetreten. Bitte versuche es später erneut.")
      } finally {
        this.submitting = false
      }
    },
  },
  computed: {
    currentYear() {
      return new Date().getFullYear()
    },
    leadSettings() {
      if (this.selectedStudio && this.selectedStudio.leadSetting) {
        return this.selectedStudio.leadSetting
      }
      return null
    },
    referralMail() {
      return this.$route.params.email ? this.$route.params.email.replace("%40", "@") : ""
    },
  },
}
</script>

<style lang="scss" scoped>
/* Core Setup */
.referral-wow-layout {
  background-color: #020202 !important;
  font-family: "Inter", "Roboto", sans-serif;
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
}

.relative-z {
  position: relative;
  z-index: 10;
}
.gap-2 {
  gap: 8px;
}
.gap-4 {
  gap: 16px;
}

/* Typography Utilities */
.tracking-tight {
  letter-spacing: -0.04em !important;
}
.tracking-widest {
  letter-spacing: 0.15em !important;
}
.lh-relaxed {
  line-height: 1.6 !important;
}

/* Cinematic Ambient Glows (Consistent with previous page) */
.ambient-glow {
  position: fixed;
  border-radius: 50%;
  filter: blur(120px);
  z-index: 1;
  opacity: 0.15;
  pointer-events: none;
}
.glow-primary {
  width: 600px;
  height: 600px;
  background: var(--v-primary-base);
  top: -200px;
  right: -200px; /* Shifted to right for balance */
  animation: float 10s ease-in-out infinite alternate;
}
.glow-secondary {
  width: 500px;
  height: 500px;
  background: #ffffff;
  bottom: -100px;
  left: -100px;
  opacity: 0.05;
}

@keyframes float {
  0% {
    transform: translate(0, 0);
  }
  100% {
    transform: translate(-50px, 50px);
  }
}

/* Glassmorphism Basics */
.glass-card {
  background: rgba(255, 255, 255, 0.03) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.inactive-box {
  max-width: 450px;
  width: 100%;
}

/* Form Specifics */
.vip-badge {
  background: rgba(var(--v-primary-base-rgb), 0.1);
  border: 1px solid rgba(var(--v-primary-base-rgb), 0.3);
  box-shadow: 0 0 15px rgba(var(--v-primary-base-rgb), 0.2);
}

.glass-input {
  margin-bottom: 10px;
}

/* Vuetify Input Overrides for Glass Effect */
::v-deep .glass-input .v-input__control .v-input__slot {
  background: rgba(0, 0, 0, 0.3) !important;
  border: 1px solid rgba(255, 255, 255, 0.05) !important;
  transition: all 0.3s ease;
}
::v-deep .glass-input.v-input--is-focused .v-input__control .v-input__slot {
  background: rgba(var(--v-primary-base-rgb), 0.05) !important;
  border-color: var(--v-primary-base) !important;
  box-shadow: 0 0 15px rgba(var(--v-primary-base-rgb), 0.2);
}

.conditions-glass {
  background: rgba(0, 0, 0, 0.4);
  border: 1px dashed rgba(255, 255, 255, 0.1);

  ::v-deep p {
    margin-bottom: 0;
  }
}

/* Buttons & Animations */
.action-btn {
  transition: transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275), box-shadow 0.2s ease;
  &:hover:not(:disabled) {
    transform: translateY(-3px) scale(1.02);
  }
}

.contact-btn {
  background: rgba(255, 255, 255, 0.03) !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
  &:hover {
    background: rgba(255, 255, 255, 0.08) !important;
  }
}

.pulse-glow {
  box-shadow: 0 0 20px rgba(var(--v-primary-base-rgb), 0.4);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(var(--v-primary-base-rgb), 0.4);
  }
  70% {
    box-shadow: 0 0 0 20px rgba(var(--v-primary-base-rgb), 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(var(--v-primary-base-rgb), 0);
  }
}

/* Success State */
.success-icon-container {
  position: relative;
  width: 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 50%;
  border: 2px solid rgba(var(--v-primary-base-rgb), 0.5);

  .success-glow {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: var(--v-primary-base);
    filter: blur(25px);
    opacity: 0.25;
  }
}

/* Divider Text */
.divider-wrapper {
  display: flex;
  align-items: center;
  text-align: center;
  &::before,
  &::after {
    content: "";
    flex: 1;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }
  .divider-text {
    padding: 0 20px;
    font-size: 0.8rem;
  }
}

/* Footer Specifics */
.footer-glass {
  background: rgba(0, 0, 0, 0.5);
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.social-icon-btn {
  transition: color 0.2s ease, transform 0.2s ease;
  &:hover {
    color: var(--v-primary-base) !important;
    transform: translateY(-2px);

    .v-image {
      filter: invert(0.5) sepia(1) saturate(5) hue-rotate(100deg) !important; /* Approximation for hover color on image */
    }
  }
}

.footer-link {
  text-transform: none !important;
  letter-spacing: 0.5px;
  &:hover {
    color: #ffffff !important;
  }
}

::v-deep .lowercase .v-btn__content {
  text-transform: none !important; /* lowercase looks sometimes weird with names, "none" preserves original casing like hello@wellcomefit.ch */
  font-size: 1rem;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.4s ease;
}
.fade-slide-enter,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(15px);
}
</style>
